/* eslint-disable max-lines */
import { useMemo } from 'react';
import {
  dateTimeToTime,
  deconstructTimeValues,
  dateTimeToDate,
  deconstructValuesFromDateString,
  fillStringWithPlaceHolders,
} from '../utils/stringHelpers.js';
import type { useGetFlags } from './useGetFlags.js';
import type { useGetPropsBasedOnType } from './useGetPropsBasedOnType.js';
import { convertToPositiveNumberIfPossible } from '../utils/numberHelpers.js';
import { monthNames, announcableCardinalDays } from '../utils/constants.js';
import type { DSControlledDateTimePickerInternalsT } from '../sharedTypes.js';

type UseGetDestructuredDateValues = (
  config: ReturnType<typeof useGetPropsBasedOnType> & ReturnType<typeof useGetFlags>,
) => {
  month: string;
  day: string;
  year: string;
  monthNum: number;
  dayNum: number;
  yearNum: number;
  announcedMonth: string;
  announcedDay: string;
  announcedYear: string;
  announcedDateValues: string;
};
// this is because it's fully controlled
// our source of truth is either the date props or dateTime props
// based on wheter it's a dateTime picker
// or a date picker (it's date picker when hideTime is true)
export const useGetDestructuredDateValues: UseGetDestructuredDateValues = ({
  appDate,
  appDateTime,
  isDateTimeSelector,
}) => {
  const dateStringFromProps = isDateTimeSelector ? dateTimeToDate(appDateTime) : appDate;
  const deconstructedValues = deconstructValuesFromDateString(dateStringFromProps);
  const { month, day, year } = deconstructedValues;
  const monthNum = convertToPositiveNumberIfPossible(month);
  const dayNum = convertToPositiveNumberIfPossible(day) as DSControlledDateTimePickerInternalsT.DateDay | -1;
  const yearNum = convertToPositiveNumberIfPossible(year);
  const announcedMonth = monthNum !== -1 ? monthNames[monthNum - 1] : 'empty month';
  const announcedDay = dayNum !== -1 ? announcableCardinalDays[dayNum] : 'empty day';
  const announcedYear = yearNum !== -1 ? fillStringWithPlaceHolders(`${yearNum}`, 4, '0') : 'empty year';
  const announcedDateValues = `${announcedMonth}, ${announcedDay}, ${announcedYear}`;

  return useMemo(
    () => ({
      month,
      day,
      year,
      monthNum,
      dayNum,
      yearNum,
      announcedMonth,
      announcedDay,
      announcedYear,
      announcedDateValues,
    }),
    [announcedDay, announcedMonth, announcedYear, announcedDateValues, day, dayNum, month, monthNum, year, yearNum],
  );
};

type UseGetDestructuredTimeValues = (
  config: ReturnType<typeof useGetPropsBasedOnType> & ReturnType<typeof useGetFlags>,
) => {
  hours: string;
  minutes: string;
  meridiem: string;
  hoursNum: number;
  minutesNum: number;
  announcedHours: string;
  announcedMinutes: string;
  announcedMeridiem: string;
  announcedTimeValues: string;
};
// this is because it's fully controlled
// our source of truth is either the time props or dateTime props
// based on wheter it's a dateTime picker
// or a time picker (it's time picker when hideDate is true)
export const useGetDestructuredTimeValues: UseGetDestructuredTimeValues = ({
  appTime,
  appDateTime,
  isDateTimeSelector,
}) => {
  const timeStringFromProps = isDateTimeSelector ? dateTimeToTime(appDateTime) : appTime;
  const deconstructedValues = deconstructTimeValues(timeStringFromProps);
  const { hours, minutes, meridiem } = deconstructedValues;
  const hoursNum = convertToPositiveNumberIfPossible(hours);
  const minutesNum = convertToPositiveNumberIfPossible(minutes);

  // 01 -> 1 ~ 09 -> 9 || 1_ -> 1 ~ 9_ -> 9 || empty...
  const announcedHours = hoursNum !== -1 ? `${hoursNum}` : 'empty hours';
  // 01 -> 1 ~ 09 -> 9 || 1_ -> 1 ~ 9_ -> 9 || empty...
  const announcedMinutes = minutesNum !== -1 ? `${minutesNum}` : 'empty minutes';
  const announcedMeridiem =
    // eslint-disable-next-line no-nested-ternary
    meridiem === 'PM' ? `post meridiem` : meridiem === 'AM' ? 'ante meridiem' : 'empty meriediem';
  const announcedTimeValues = `${announcedHours}, ${announcedMinutes}, ${announcedMeridiem}`;

  return useMemo(
    () => ({
      hours,
      minutes,
      meridiem,
      hoursNum,
      minutesNum,
      announcedHours,
      announcedMinutes,
      announcedMeridiem,
      announcedTimeValues,
    }),
    [
      hours,
      minutes,
      meridiem,
      hoursNum,
      minutesNum,
      announcedHours,
      announcedMinutes,
      announcedMeridiem,
      announcedTimeValues,
    ],
  );
};

type UseGetDestructuredValues = (
  config: ReturnType<typeof useGetPropsBasedOnType> & ReturnType<typeof useGetFlags>,
) => ReturnType<typeof useGetDestructuredDateValues> & ReturnType<typeof useGetDestructuredTimeValues>;

export const useGetDestructuredValues: UseGetDestructuredValues = (config) => {
  const dateValues = useGetDestructuredDateValues(config);
  const timeValues = useGetDestructuredTimeValues(config);

  return useMemo(
    () => ({
      ...dateValues,
      ...timeValues,
    }),
    [dateValues, timeValues],
  );
};
